import { ActionIcon, Group, TextInput, Tooltip } from "@mantine/core";
import { IconSolarPanel2, IconTrash } from "@tabler/icons-react";
import { t } from "i18next";
import { apiWithoutCamelization } from "../../../../api";
import urls from "../../../../urls";
import { type PVPlantWithMeter } from "../../SiteSetupAssistant.types";

interface PlantWithSEEProps {
  pvPlants: Array<PVPlantWithMeter>;
  index: number;
  error?: string;
  onChange: (data: Array<PVPlantWithMeter>, index: number) => void;
  onDelete: (index: number) => void;
  onSetError: (error: string | null, index: number) => void;
}

function PlantWithSEE({
  error,
  index,
  pvPlants,
  onChange,
  onDelete,
  onSetError
}: PlantWithSEEProps) {
  const pvPlant = pvPlants[index];

  async function validateSeeNumberLight(
    value: string | undefined,
    index: number
  ) {
    if (value === "" || typeof value === "undefined" || value === null) {
      onSetError(null, index);
    } else if (
      pvPlants.find(
        (pv, pvIndex) => pvIndex !== index && pv?.see_number === value
      )
    ) {
      onSetError(t("errors.SiteSetupAssistant.DuplicateMastrNumber"), index);
    } else {
      const seeNumberRegex = /^(SEE|EEG)\d{12}$/;
      if (!seeNumberRegex.test(value)) {
        onSetError(
          t("errors.SiteSetupAssistant.OfflineMastrNumberValidationFailed"),
          index
        );
      } else {
        try {
          const validationSuccess = await validateSeeNumberDeep(value);
          onSetError(validationSuccess, index);
        } catch (error) {
          onSetError(error, index);
        }
      }
    }
  }

  async function validateSeeNumberDeep(value: string): Promise<string | null> {
    try {
      await apiWithoutCamelization.post(
        urls.apiWithoutCamelization.validateSEENumber(),
        { see_number: value }
      );
      return Promise.resolve(null);
    } catch (error) {
      const errorText = t(
        `errors.SiteSetupAssistant.${error.response.data[0]}`
      );
      return Promise.reject(errorText);
    }
  }

  function handleChangeSeeNumber(index: number, value: string) {
    const updatedPvPlants = pvPlants.map((pvPlant, idx) => {
      if (idx === index) {
        return {
          ...pvPlant,
          see_number: value,
          meter: { ...pvPlant.meter, name: "Zähler " + value }
        };
      }
      return pvPlant;
    });
    onChange(updatedPvPlants, index);
  }

  return (
    <Group align="end" className="PlantWithSEE single-plant" gap="md">
      <Group pb={error ? "xl" : "md"}>
        <IconSolarPanel2 />
      </Group>
      <TextInput
        error={error}
        label="SEE-/EEG-Nummer"
        placeholder="z.Bsp. SEE111111118666"
        value={pvPlant.see_number ?? undefined}
        onBlur={() => validateSeeNumberLight(pvPlant.see_number ?? "", index)}
        onChange={(event) => handleChangeSeeNumber(index, event.target.value)}
      />
      <Tooltip label="Anlage löschen">
        <ActionIcon
          color="error"
          mb={error ? "lg" : 6}
          variant="transparent"
          onClick={() => onDelete(index)}
        >
          <IconTrash size="1.3rem" />
        </ActionIcon>
      </Tooltip>
    </Group>
  );
}

export { PlantWithSEE };
